// @flow
/** @jsx jsx */

import { Fragment } from 'react';

import LogoImage from '@zapier/common-components/src/components/LogoImage';
import { Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import type { Node } from 'react';

const itemNameCss = css({
  fontSize: '14px',
  color: Colors.deepspace,
  marginLeft: '10px',
});

export type BannerType =
  | 'beta'
  | 'by-invite'
  | 'deprecated'
  | 'premium'
  | 'upcoming';

export type Item = {
  key?: string,
  name: ?string,
  subtitle?: string | Node,
  banner?: ?BannerType,
  isDisabled?: boolean,
  logoClass?: string,
  value?: string,
  order?: ?number,
  shouldRenderSubtitle?: boolean,
  type?: string,
};

export type Props = {
  /** The logoClass of the currently selected service. */
  selectedItem?: Item,
  /** Are we still loading services? If true display a 'Loading...' message. */
  isLoading: boolean,
  /** Placeholder text for the input element */
  placeholder: string,
  /** Given a logo class / service identifier, return the human name of the service. */
  getLabelForItem: (item: Item) => string,
};

/**
 * Renders a logo to the right of selection content. For use as the
 * `SelectionComponent` prop passed to `SearchableSelectField`, when you
 * want to render say the selected app icon along with the selection text.
 *
 * Handles isLoading, showing placeholder when no selected item.
 */
const SelectItem = ({
  selectedItem,
  isLoading,
  placeholder,
  getLabelForItem,
}: Props) => {
  if (isLoading) {
    return <div>Loading &hellip;</div>;
  }
  if (!selectedItem) {
    return (
      <div aria-hidden={true} tabIndex={-1}>
        {placeholder}
      </div>
    );
  }

  return (
    <Fragment>
      {selectedItem.logoClass && (
        <LogoImage app={selectedItem.logoClass} size={16} />
      )}
      <span css={itemNameCss}>{getLabelForItem(selectedItem)}</span>
    </Fragment>
  );
};

export default SelectItem;
